import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Users, Search, Mail, Shield, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function UsersTab({ token }) {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('latest'); // 'latest' or 'oldest'
  const [roleFilter, setRoleFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        setUsers(res.data);
        setFilteredUsers(res.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  // Apply filters and sorting
  useEffect(() => {
    let result = [...users];
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(u => 
        u.full_name?.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term)
      );
    }
    
    // Role filter
    if (roleFilter !== 'all') {
      result = result.filter(u => u.role === roleFilter);
    }
    
    // Sort by date
    result.sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return sortOrder === 'latest' ? dateB - dateA : dateA - dateB;
    });
    
    setFilteredUsers(result);
  }, [users, searchTerm, sortOrder, roleFilter]);

  const roleOptions = ['all', 'candidate', 'examiner', 'admin', 'institutional', 'curriculum', 'examination_center', 'accreditation'];

  return (
    <div data-testid="admin-users-tab" className="space-y-4">
      {/* Filters Row */}
      <div className="flex flex-wrap gap-3 items-center bg-slate-50 p-3 rounded-sm">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 rounded-sm text-sm"
              data-testid="users-search"
            />
          </div>
        </div>
        <div className="w-[150px]">
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="rounded-sm text-sm">
              <SelectValue placeholder="Filter by Role" />
            </SelectTrigger>
            <SelectContent>
              {roleOptions.map(role => (
                <SelectItem key={role} value={role} className="text-sm">
                  {role === 'all' ? 'All Roles' : role.charAt(0).toUpperCase() + role.slice(1).replace('_', ' ')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-[150px]">
          <Select value={sortOrder} onValueChange={setSortOrder}>
            <SelectTrigger className="rounded-sm text-sm">
              <SelectValue placeholder="Sort Order" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="latest" className="text-sm">Latest First</SelectItem>
              <SelectItem value="oldest" className="text-sm">Oldest First</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => { setSearchTerm(''); setSortOrder('latest'); setRoleFilter('all'); }}
          className="rounded-sm text-xs"
        >
          <RefreshCw className="h-3 w-3 mr-1" /> Clear
        </Button>
      </div>

      {/* Stats */}
      <div className="flex gap-2 text-xs text-slate-500">
        <span>Total: <strong>{users.length}</strong></span>
        <span>|</span>
        <span>Showing: <strong>{filteredUsers.length}</strong></span>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500 py-8 text-center">Loading users...</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F8FAFC]">
              <TableHead className="text-xs font-bold">Name</TableHead>
              <TableHead className="text-xs font-bold">Email</TableHead>
              <TableHead className="text-xs font-bold">Role</TableHead>
              <TableHead className="text-xs font-bold">Registered</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-sm text-slate-500 py-8">No users found matching your filters.</TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((u) => (
                <TableRow key={u.id}>
                  <TableCell className="text-sm font-medium text-[#1B2A4A]">{u.full_name}</TableCell>
                  <TableCell className="text-sm text-slate-600">{u.email}</TableCell>
                  <TableCell>
                    <Badge className={`text-xs rounded-sm ${
                      u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 
                      u.role === 'examiner' ? 'bg-blue-100 text-blue-700' : 
                      u.role === 'examination_center' ? 'bg-teal-100 text-teal-700' :
                      u.role === 'institutional' ? 'bg-amber-100 text-amber-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>{u.role}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">{new Date(u.created_at).toLocaleDateString()}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

export default UsersTab;
